import { AnalyticsBrowser, Analytics } from '@segment/analytics-next';
import { logger } from '../utils/logger';
import { shouldUseDirectAnalytics } from '../utils/urlUtils';

const SegmentKey = import.meta.env.VITE_SEGMENT_KEY;

// Singleton pattern for Segment initialization
class SegmentSingleton {
  private static instance: SegmentSingleton;
  private initialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private analytics: Analytics | undefined = undefined;

  private constructor() {}

  public static getInstance(): SegmentSingleton {
    if (!SegmentSingleton.instance) {
      SegmentSingleton.instance = new SegmentSingleton();
    }
    return SegmentSingleton.instance;
  }

  public async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) {
      return Promise.resolve();
    }

    // If initialization is in progress, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    if (!SegmentKey || SegmentKey.length <= 10) {
      logger.warn('Segment key is missing or appears to be a placeholder. Analytics will be disabled.');
      return;
    }

    // Test network connectivity first
    await this.testNetworkConnectivity();

    const retries = 3;
    
    for (let i = 0; i < retries; i++) {
      try {
        const config: any = {
          writeKey: SegmentKey
        };

        let integrations: any = {};

        // Only use proxy if not on localhost AND on first attempt
        if (!shouldUseDirectAnalytics() && i === 0) {
          // Segment CDN expects path only, but API host expects full hostname
          const cdnProxyUrl = `/segment-cdn-proxy`;
          const apiProxyHost = `${window.location.hostname}/segment-api-proxy`;
          
          // Configure proxy for CDN endpoint (where Segment script is loaded from)
          config.cdnURL = cdnProxyUrl;
          
          // Configure tracking API proxy (needs full hostname format)
          config.apiHost = apiProxyHost;  // For general API calls
          config.host = apiProxyHost;     // Alternative property name
          
          // Also try integrations-level configuration
          integrations = {
            'Segment.io': {
              apiHost: apiProxyHost,
              host: apiProxyHost,
              endpoint: apiProxyHost
            }
          };

          // Enhanced logging for debugging
          console.log('🔧 Segment proxy configured with UPDATED separate CDN/API paths v2');
          console.log('CDN Proxy URL (path only):', cdnProxyUrl);
          console.log('API Proxy Host (hostname/path):', apiProxyHost);
          console.log('Final CDN URL:', config.cdnURL);
          console.log('Final API Host:', config.apiHost);
          console.log('Final Host:', config.host);
          console.log('Integrations:', integrations);
          console.log('Full config being used:', JSON.stringify(config, null, 2));
          
          logger.info('🚀 Segment proxy attempt:', {
            cdnProxyUrl: `${window.location.origin}${cdnProxyUrl}`,
            apiProxyHost: `https://${apiProxyHost}`,
            cdnURL: config.cdnURL,
            apiHost: config.apiHost,
            host: config.host,
            integrations,
            attempt: i + 1
          });
        } else {
          // Fallback or localhost: use direct connections
          delete config.cdnURL;
          delete config.apiHost;
          delete config.host;
          integrations = {};
          
          const reason = shouldUseDirectAnalytics() ? 'localhost detected' : `fallback attempt ${i + 1}`;
          logger.info(`🔧 Segment direct configuration (${reason})`, {
            cdnURL: 'cdn.segment.com (default)',
            apiHost: 'api.segment.io (default)',
            attempt: i + 1
          });
        }

        logger.info(`🚀 About to call AnalyticsBrowser.load with config:`, { 
          config: { ...config, writeKey: SegmentKey?.substring(0, 8) + '...' }, 
          attempt: i + 1,
          proxyEnabled: !shouldUseDirectAnalytics() && i === 0,
          integrations
        });

        console.log('🔍 Calling AnalyticsBrowser.load with:', config, 'integrations:', integrations);
        
        // Add detailed error capturing
        let analyticsInstance;
        try {
          const result = await AnalyticsBrowser.load(config, {
            integrations
          });
          analyticsInstance = result[0];
        } catch (loadError: any) {
          // Enhanced error logging
          console.error('❌ AnalyticsBrowser.load failed with detailed info:', {
            error: loadError,
            message: loadError?.message,
            stack: loadError?.stack,
            name: loadError?.name,
            cause: loadError?.cause,
            config,
            integrations,
            attempt: i + 1
          });
          
          // Try to capture network-related information
          if (loadError?.message?.includes('Unexpected token')) {
            console.error('🔍 HTML response detected - likely network blocking or wrong URL');
            
            // Test the URLs that Segment might be trying to access
            await this.debugSegmentUrls(config);
          }
          
          throw loadError;
        }
        
        console.log('✅ AnalyticsBrowser.load completed successfully');
        logger.info('✅ AnalyticsBrowser.load completed successfully');
        
        this.analytics = analyticsInstance;
        this.initialized = true;
        
        const loadMethod = i === 0 && !shouldUseDirectAnalytics() ? 'with proxy' : 'without proxy';
        logger.info(`✅ Segment initialized successfully ${loadMethod} on attempt ${i + 1}`);
        
        // Test if analytics instance is working
        if (this.analytics && typeof this.analytics.track === 'function') {
          logger.info('📊 Segment analytics instance is ready for tracking');
        } else {
          logger.warn('⚠️ Segment analytics instance may not be fully functional');
        }
        
        return;
      } catch (error: any) {
        const errorDetails = {
          message: error?.message,
          name: error?.name,
          stack: error?.stack,
          cause: error?.cause,
          attempt: i + 1,
          usingProxy: !shouldUseDirectAnalytics() && i === 0
        };
        
        logger.error(`Segment initialization attempt ${i + 1} failed:`, errorDetails);
        console.error(`❌ Detailed error for attempt ${i + 1}:`, errorDetails);
        
        // If first attempt failed and we were using proxy, try without proxy
        if (i === 0 && !shouldUseDirectAnalytics()) {
          logger.warn('Proxy initialization failed, will try direct connection as fallback', error?.message);
        } else if (i === retries - 1) {
          logger.error('All retries failed for Segment initialization. Analytics will be disabled.', errorDetails);
          // Don't throw error - just log it and continue without analytics
          return;
        }
      }
    }
  }

  private async testNetworkConnectivity(): Promise<void> {
    console.log('🌐 Testing network connectivity to Segment endpoints...');
    
    // Test proxy with a real Segment endpoint
    const realSegmentPath = `v1/projects/${SegmentKey}/settings`;
    const proxyUrl = `${window.location.origin}/segment-proxy/${realSegmentPath}`;
    
    try {
      console.log(`🔍 Testing proxy with real Segment path: ${proxyUrl}`);
      const response = await fetch(proxyUrl, { 
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'X-Target-URL': `https://cdn.segment.com/${realSegmentPath}`
        }
      });
      
      const responseText = await response.text();
      const isHTML = responseText.trim().toLowerCase().startsWith('<!doctype') || responseText.trim().toLowerCase().startsWith('<html');
      
      console.log(`📊 Proxy test response:`, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        bodyPreview: responseText.substring(0, 300),
        isHTML,
        bodyLength: responseText.length
      });
      
      if (response.ok && !isHTML) {
        console.log(`✅ Proxy appears to be working correctly for Segment`);
      } else if (isHTML) {
        console.error(`🚨 CRITICAL: Proxy returned HTML instead of JSON - this is the root cause!`);
        console.error(`🔍 This means either:
1. The Cloudflare Worker isn't routing Segment requests correctly
2. Segment endpoints are being blocked/redirected by firewall
3. The X-Target-URL header isn't working as expected`);
      } else {
        console.warn(`⚠️ Proxy returned non-200 response: ${response.status}`);
      }
        
    } catch (error: any) {
      console.error(`❌ Proxy test failed:`, {
        error: error.message,
        name: error.name,
        type: error.constructor.name,
        stack: error.stack?.split('\n')[0] // First line of stack trace
      });
    }
  }

  private async debugSegmentUrls(config: any): Promise<void> {
    console.log('🔍 Debugging Segment URL construction...');
    
    // List of URLs that Segment might try to access
    const potentialUrls = [
      'https://cdn.segment.com/analytics.js/v1/MvVzHNZD1hoGE9EWhuJ9oCmaSDVUyg9w/analytics.min.js',
      'https://cdn.segment.com/v1/projects/MvVzHNZD1hoGE9EWhuJ9oCmaSDVUyg9w/settings',
      'https://api.segment.io/v1/projects/MvVzHNZD1hoGE9EWhuJ9oCmaSDVUyg9w/settings'
    ];

    if (config.cdnURL) {
      potentialUrls.push(
        `https://${config.cdnURL}/analytics.js/v1/MvVzHNZD1hoGE9EWhuJ9oCmaSDVUyg9w/analytics.min.js`,
        `https://${config.cdnURL}/v1/projects/MvVzHNZD1hoGE9EWhuJ9oCmaSDVUyg9w/settings`
      );
    }

    for (const url of potentialUrls) {
      try {
        console.log(`🔍 Testing potential Segment URL: ${url}`);
        const response = await fetch(url, { method: 'HEAD' });
        console.log(`📊 ${url} - Status: ${response.status} ${response.statusText}`);
      } catch (error: any) {
        console.warn(`❌ ${url} - Error: ${error.message}`);
      }
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public getAnalytics(): Analytics | undefined {
    return this.analytics;
  }

  public async testConnectivity(): Promise<void> {
    const isLocal = shouldUseDirectAnalytics();
    
    logger.info('🧪 Testing Segment connectivity...', {
      isLocalhost: isLocal,
      origin: window.location.origin,
      initialized: this.initialized
    });

    if (!this.analytics) {
      logger.error('Cannot test connectivity: Segment not initialized');
      return;
    }

    try {
      // Send a test track event
      this.analytics.track('Segment Connectivity Test', {
        timestamp: new Date().toISOString(),
        test: true,
        proxy_configured: !isLocal,
        origin: window.location.origin
      });
      
      logger.info('✅ Segment connectivity test event sent');
    } catch (error) {
      logger.error('❌ Segment connectivity test failed:', error);
    }
  }

  public getProxyStatus() {
    const isLocal = shouldUseDirectAnalytics();
    return {
      isLocalhost: isLocal,
      proxyConfigured: !isLocal,
      origin: window.location.origin,
      hostname: window.location.hostname,
      initialized: this.initialized,
      segmentKey: SegmentKey?.substring(0, 8) + '...',
      hasAnalytics: !!this.analytics
    };
  }

  public async testProxyDirectly(): Promise<void> {
    console.log('🧪 Testing Cloudflare Worker proxy directly...');
    
    const proxyUrl = `${window.location.origin}/segment-proxy`;
    const testUrls = [
      `${proxyUrl}/analytics.js/v1/${SegmentKey}/analytics.min.js`,
      `${proxyUrl}/v1/projects/${SegmentKey}/settings`,
      `${proxyUrl}/v1/t`, // tracking endpoint
      `${proxyUrl}/health`,
      proxyUrl // base proxy URL
    ];

    for (const url of testUrls) {
      try {
        console.log(`🔍 Testing proxy URL: ${url}`);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json'
          }
        });
        
        const responseText = await response.text();
        const isHTML = responseText.trim().toLowerCase().startsWith('<!doctype') || 
                      responseText.trim().toLowerCase().startsWith('<html');
        
        console.log(`📊 Proxy response from ${url}:`, {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          bodyPreview: responseText.substring(0, 300),
          bodyLength: responseText.length,
          isHTML,
          contentType: response.headers.get('content-type')
        });
        
        if (isHTML) {
          console.warn(`⚠️ Proxy returned HTML for ${url} - this is likely the issue!`);
          console.log('Full HTML response:', responseText);
        }
        
      } catch (error: any) {
        console.error(`❌ Proxy test failed for ${url}:`, {
          error: error.message,
          name: error.name,
          stack: error.stack
        });
      }
    }
  }
}

// Export debugging functions to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugSegment = {
    testConnectivity: async () => {
      console.log('🧪 Manual connectivity test initiated...');
      const instance = SegmentSingleton.getInstance();
      await (instance as any).testNetworkConnectivity();
    },
    testProxy: async () => {
      console.log('🧪 Manual proxy test initiated...');
      const instance = SegmentSingleton.getInstance();
      await (instance as any).testProxyDirectly();
    },
    testUrls: async () => {
      console.log('🧪 Manual URL debugging initiated...');
      const instance = SegmentSingleton.getInstance();
      const config = { cdnURL: `${window.location.hostname}/segment-proxy` };
      await (instance as any).debugSegmentUrls(config);
    },
    getStatus: () => {
      const instance = SegmentSingleton.getInstance();
      return (instance as any).getProxyStatus();
    },
    reinitialize: async () => {
      console.log('🔄 Forcing Segment reinitialization...');
      const instance = SegmentSingleton.getInstance();
      // Reset the instance state
      (instance as any).initialized = false;
      (instance as any).initializationPromise = null;
      (instance as any).analytics = undefined;
      await instance.initialize();
    }
  };
}

export default SegmentSingleton;